import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import api from '../../../services/api';

const T = {
  bg:      '#f2f6f3',
  white:   '#ffffff',
  ink:     '#0c1810',
  ink2:    '#1e3328',
  subtle:  '#4d6b58',
  muted:   '#8ba898',
  border:  '#dae6df',
  borderL: '#eaf2ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e4f5ec',
  whatsapp: '#25D366',
};

const WHATSAPP_TEAL = '#075E54';
const OUTBOUND_BG = '#DCF8C6';
const INBOUND_BG = '#FFFFFF';
const CHAT_BG = '#ECE5DD';

const formatTime = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDateLabel = (isoString) => {
  if (!isoString) return '';
  const msgDate = new Date(isoString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(msgDate, today)) return 'Today';
  if (isSameDay(msgDate, yesterday)) return 'Yesterday';
  return msgDate.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(msgDate.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
  });
};

const getDateKey = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const buildChatItems = (messages) => {
  if (!messages || messages.length === 0) return [];

  const sorted = [...messages].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );

  const items = [];
  let lastDateKey = null;

  for (const msg of sorted) {
    const dk = getDateKey(msg.timestamp);
    if (dk !== lastDateKey) {
      items.push({
        _type: 'date',
        _id: `date-${dk}`,
        label: formatDateLabel(msg.timestamp),
      });
      lastDateKey = dk;
    }
    items.push({ _type: 'msg', _id: String(msg.id), ...msg });
  }

  // Reverse for column-reverse display (newest first at array start)
  return items.reverse();
};

const DateSeparator = React.memo(({ label }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
    <Box
      sx={{
        bgcolor: 'rgba(225,219,212,0.92)',
        px: 1.75,
        py: 0.6,
        borderRadius: '8px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#54656f', letterSpacing: 0.1 }}>
        {label}
      </Typography>
    </Box>
  </Box>
));

const MessageBubble = React.memo(({ message, replyContext }) => {
  const isOutbound = message.direction === 'outbound';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOutbound ? 'flex-end' : 'flex-start',
        mb: 0.25,
        px: 0.75,
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          px: 1.25,
          pt: 0.75,
          pb: 1,
          borderRadius: '8px',
          bgcolor: isOutbound ? OUTBOUND_BG : INBOUND_BG,
          boxShadow: '0 1px 1px rgba(0,0,0,0.04)',
          ...(isOutbound
            ? { borderTopRightRadius: 0, mr: 0.25 }
            : { borderTopLeftRadius: 0, ml: 0.25 }),
        }}
      >
        {message.reply_to && (
          <Box
            sx={{
              borderLeft: '3px solid',
              borderLeftColor: isOutbound ? '#34B7F1' : T.whatsapp,
              pl: 1,
              pr: 1,
              pb: 1,
              mb: 1,
              bgcolor: isOutbound ? 'rgba(52, 183, 241, 0.1)' : 'rgba(37, 211, 102, 0.1)',
              borderRadius: '4px 0 0 4px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                color: isOutbound ? '#34B7F1' : T.whatsapp,
                display: 'block',
                mb: 0.5,
              }}
            >
              {replyContext || 'Business'}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: T.subtle,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.3,
              }}
            >
              {message.reply_to.content || '[Message]'}
            </Typography>
          </Box>
        )}

        <Typography
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 14,
            color: '#111b21',
            lineHeight: 1.35,
          }}
        >
          {message.content || '[Media]'}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.25,
          }}
        >
          <Typography component="span" sx={{ fontSize: 11, color: '#667781' }}>
            {formatTime(message.timestamp)}
          </Typography>
          {isOutbound && (
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 500,
                ml: 0.25,
                color: message.status === 'read' ? '#53bdeb' : '#667781',
              }}
            >
              {message.status === 'sent' ? '✓' : '✓✓'}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
});

const LeadWhatsApp = ({ leadId, lead }) => {
  const displayName = lead?.contact_info?.name?.trim() || null;

  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [whatsappConnected, setWhatsappConnected] = useState(false);

  const chatItems = useMemo(() => buildChatItems(messages), [messages]);

  useEffect(() => {
    checkWhatsAppConnection();
  }, []);

  useEffect(() => {
    if (leadId && whatsappConnected) {
      fetchConversations();
      const interval = setInterval(fetchConversations, 30000);
      return () => clearInterval(interval);
    }
  }, [leadId, whatsappConnected]);

  const checkWhatsAppConnection = async () => {
    try {
      const response = await api.get('/lms/integrations/');
      const data = Array.isArray(response.data) ? response.data : [];
      const whatsappIntegration = data.find(integration => integration.id === 'whatsapp');
      setWhatsappConnected(whatsappIntegration?.connected || false);
    } catch (error) {
      console.error('Error checking WhatsApp connection:', error);
      setWhatsappConnected(false);
    }
  };

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/lms/whatsapp/conversations/', {
        params: { lead_id: leadId }
      });
      const leadConversations = response.data.conversations || [];
      setConversations(leadConversations);

      if (leadConversations.length > 0 && !selectedConversation) {
        handleSelectConversation(leadConversations[0]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      const response = await api.get(`/lms/whatsapp/conversations/${conversationId}/messages/`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = async (conversation) => {
    try {
      const response = await api.get(`/lms/whatsapp/conversations/${conversation.id}/`);
      setSelectedConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      setSelectedConversation(conversation);
    }

    await fetchMessages(conversation.id);

    if (conversation.unread_count > 0) {
      try {
        await api.patch(`/lms/whatsapp/conversations/${conversation.id}/`, {
          unread_count: 0
        });
        setConversations(prev =>
          prev.map(c => c.id === conversation.id ? { ...c, unread_count: 0 } : c)
        );
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  if (!whatsappConnected) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Alert severity="info">
          WhatsApp is not connected. Please connect your WhatsApp account in Settings → Integrations.
        </Alert>
      </Box>
    );
  }

  if (loading && conversations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: T.green }} />
      </Box>
    );
  }

  if (!loading && conversations.length === 0) {
    return (
      <Box sx={{ px: 2, py: 5, textAlign: 'center' }}>
        <WhatsAppIcon sx={{ fontSize: 64, color: T.muted, mb: 2, opacity: 0.5 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink2, mb: 1 }}>
          No WhatsApp conversations yet
        </Typography>
        <Typography sx={{ fontSize: 12, color: T.muted }}>
          WhatsApp messages with this lead will appear here
        </Typography>
      </Box>
    );
  }

  const replyContext = selectedConversation?.integration_info?.display_name || 'Business';

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid ${T.border}`,
          borderRadius: '12px',
          overflow: 'hidden',
          mx: 2,
          my: 1,
          minHeight: 0,
        }}
      >
        {/* ── Chat Header ── */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: WHATSAPP_TEAL,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'rgba(255,255,255,0.25)',
              width: 40,
              height: 40,
              fontSize: 18,
              fontWeight: 700,
              color: '#fff',
            }}
          >
            {(displayName || selectedConversation?.contact_name || selectedConversation?.contact_phone)
              ?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 15, fontWeight: 600, color: '#fff' }}
              noWrap
            >
              {displayName || selectedConversation?.contact_name || selectedConversation?.contact_phone}
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }} noWrap>
              {selectedConversation?.contact_phone}
            </Typography>
          </Box>

          {/* Conversation switcher chips */}
          {conversations.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
              {conversations.map((conv) => {
                const active = selectedConversation?.id === conv.id;
                return (
                  <Chip
                    key={conv.id}
                    label={
                      conv.integration_info?.phone_number
                        ? conv.integration_info.phone_number.slice(-4)
                        : `#${conv.id}`
                    }
                    size="small"
                    onClick={() => handleSelectConversation(conv)}
                    sx={{
                      height: 24,
                      fontSize: 11,
                      fontWeight: active ? 700 : 500,
                      bgcolor: active ? '#fff' : 'rgba(255,255,255,0.15)',
                      color: active ? WHATSAPP_TEAL : 'rgba(255,255,255,0.85)',
                      border: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: active ? '#fff' : 'rgba(255,255,255,0.25)',
                      },
                      ...(conv.unread_count > 0 && !active
                        ? {
                            '&::after': {
                              content: `"${conv.unread_count}"`,
                              ml: 0.5,
                              fontSize: 9,
                              fontWeight: 700,
                              bgcolor: T.whatsapp,
                              color: '#fff',
                              borderRadius: '50%',
                              width: 16,
                              height: 16,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            },
                          }
                        : {}),
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {/* ── Messages Area ── */}
        {loadingMessages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, bgcolor: CHAT_BG }}>
            <CircularProgress sx={{ color: T.whatsapp }} />
          </Box>
        ) : chatItems.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: CHAT_BG }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 48, mb: 1, opacity: 0.4 }}>💬</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                No messages in this conversation
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column-reverse',
              bgcolor: CHAT_BG,
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23ECE5DD\'/%3E%3Cpath d=\'M20 20h60v60H20z\' fill=\'none\' stroke=\'%23d1d7db\' stroke-width=\'0.5\' opacity=\'0.1\'/%3E%3C/svg%3E")',
              py: 1,
            }}
          >
            {chatItems.map((item) =>
              item._type === 'date' ? (
                <DateSeparator key={item._id} label={item.label} />
              ) : (
                <MessageBubble
                  key={item._id}
                  message={item}
                  replyContext={replyContext}
                />
              ),
            )}
          </Box>
        )}

        {/* ── Footer ── */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: '#f0fdf4',
            borderTop: '1px solid #dcfce7',
          }}
        >
          <Typography sx={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>
            Messages are sent via WhatsApp templates from the CRM
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default LeadWhatsApp;
